import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FloorService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { IFloor } from "../../shared/interfaces/floor/IFloor";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
  name: yup.string().required("validation.required"),
  floorNumber: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return isNaN(originalValue) ? undefined : Number(originalValue);
    })
    .notRequired()
    .test("floor-number-format", "validation.invalidNumber", function (value) {
      if (value === undefined || value === null) return true; // Allow empty values
      return !isNaN(value) && Number.isInteger(value);
    }),
});

const FloorForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [floorData, setFloorData] = useState<IFloor | null>(null);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFloor>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: "",
      floorNumber: undefined,
    },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = async (data: IFloor) => {
    try {
      if (isEdit) {
        await FloorService.update(data, +id!);
      } else {
        await FloorService.create(data);
      }
      SuccessToast(t(`floor.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/floors");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const fetchFloor = async () => {
        try {
          const response = await FloorService.getSingle(+id!);
          setFloorData(response);
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchFloor();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (floorData) {
      reset(floorData);
    }
  }, [floorData, reset]);

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <Form.Label className="required">
                      {t("floor.name")}
                    </Form.Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="text"
                            isInvalid={!!fieldState.error}
                            placeholder={t("floor.enterName")}
                            {...field}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col sm={12} xl={6}>
                    <Form.Label>{t("floor.floorNumber")}</Form.Label>
                    <Controller
                      name="floorNumber"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="number"
                            step="1"
                            isInvalid={!!fieldState.error}
                            placeholder={t("floor.enterFloorNumber")}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === "" ? undefined : Number(e.target.value),
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`floor.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/floors")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default FloorForm;
